import { atom } from "jotai";
import { z } from "zod";
import { focusModeAssetAtom, iframeFocusModeAtom } from "@zapier/canvas-state";
import { JotaiTool } from "../../index";

export const FocusModePostMessageParams = z.object({
  type: z
    .literal("canvas:copilot-update")
    .describe(
      "Message type - always use 'canvas:copilot-update' to signal iframe refresh",
    ),
  stepId: z
    .string()
    .optional()
    .describe(
      "Required for Zap assets: The specific Zap step ID that was just edited/configured",
    ),
  subStepKey: z
    .enum(["setup", "fields"])
    .optional()
    .describe(
      "Required for Zap assets: Which part was edited - 'setup' for app/event/auth changes, 'fields' for field mapping changes, 'filter' for filter or conditional logic settings, 'path' for path branch configuration, 'sample' for step testing",
    ),
  interfaceId: z
    .string()
    .optional()
    .describe(
      "Required for Interface assets: The interface project ID that was just edited",
    ),
  pageId: z
    .string()
    .optional()
    .describe(
      "Required for Interface assets: The specific page ID within the interface that was just edited",
    ),
  tableId: z
    .string()
    .optional()
    .describe(
      "Required for Table assets: The table ID that was just edited (fields, records, etc.)",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Refreshing editor...' or 'Updating interface...'",
    ),
});

export type FocusModePostMessageParamsType = z.infer<
  typeof FocusModePostMessageParams
>;

const focusModePostMessageExecuteAtom = atom(
  null,
  async (get, _, { args }: { args: FocusModePostMessageParamsType }) => {
    try {
      const params = FocusModePostMessageParams.parse(args);
      const { stepId, subStepKey, interfaceId, pageId, tableId } = params;

      const iframe = get(iframeFocusModeAtom);

      if (!iframe || !iframe.contentWindow) {
        return {
          content: [
            {
              type: "text",
              text: "Error: Canvas is not currently in focus mode or iframe not available",
            },
          ],
        };
      }

      const focusModeAsset = get(focusModeAssetAtom);

      const payload: FocusModePostMessageParamsType = {
        type: "canvas:copilot-update",
      };

      if (focusModeAsset.type === "zap") {
        payload.stepId = stepId;
        payload.subStepKey = subStepKey;
      }

      if (focusModeAsset.type === "interface") {
        payload.interfaceId = interfaceId;
        payload.pageId = pageId;
      }

      if (focusModeAsset.type === "table") {
        payload.tableId = tableId;
      }

      // Send the message to the iframe's parent window
      iframe.contentWindow.postMessage(
        {
          ...payload,
        },
        "*",
      );

      return {
        content: [
          {
            type: "text",
            text: `Successfully sent focus mode message to ${focusModeAsset.type} asset`,
          },
        ],
      };
    } catch (error) {
      return {
        content: [
          {
            type: "text",
            text: `Error sending focus mode message: ${error instanceof Error ? error.message : String(error)}`,
          },
        ],
      };
    }
  },
);

export const focusModePostMessageAtom = atom<
  JotaiTool<FocusModePostMessageParamsType>
>(() => ({
  name: "focus_mode_post_message",
  parameters: FocusModePostMessageParams,
  execute: focusModePostMessageExecuteAtom,
  description: focusModePostMessageDescription,
}));

export const focusModePostMessageDescription = `CRITICAL: Call this tool immediately after successfully editing any asset (Zap, Interface, or Table) when Canvas is running in focus mode.

PURPOSE: Notifies the asset iframe that changes have been made so it can refresh and display the updated content.

WHEN TO CALL:
- Immediately after successful Zap edits (step configuration, field mapping, step testing, etc.)
- Immediately after successful Interface edits (page updates, form changes, etc.) 
- Immediately after successful Table edits (field additions, record updates, etc.)
- ANY time you make backend changes to assets while in focus mode

WHAT IT DOES:
- Sends a postMessage to the iframe telling it "something changed, please refresh"
- The iframe receives this signal and updates its display with the latest data
- Keeps the iframe content in sync with Canvas changes

PARAMETERS REQUIRED:
- type: Always "canvas:copilot-update"
- For Zap assets: stepId (the step you just edited), subStepKey ('setup' for app, event and auth, 'fields' for field mapping, 'filter' for filter or conditional logic settings, 'path' for path branch configuration, 'sample' for step testing)
- For Interface assets: interfaceId (the interface ID), pageId (the page you just edited)  
- For Table assets: tableId (the table you just edited)

The tool automatically detects the asset type from focusModeAssetAtom and only sends relevant parameters.

IMPORTANT: This tool only works when Canvas is in focus mode with an active iframe. If not in focus mode, the tool will return an error.`;

focusModePostMessageAtom.debugLabel = "focusModePostMessageAtom";
